import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth } from "../hooks/useAuth";
import { useLeaderboardVisible } from "../hooks/useUnits";
import { SourceLink } from "../pages/site/SiteLayout";

type NavItem = { to: "/" | "/board" | "/dashboard" | "/console" | "/trim-room"; label: string; icon: string };

const ALL_ITEMS: NavItem[] = [
  { to: "/trim-room", label: "Room", icon: "M3 21h18M5 21V7l7-4 7 4v14M9 21v-7h6v7M9 8h.01M15 8h.01" },
  { to: "/", label: "Log", icon: "M8 3H5v18h14V3h-3M8 2h8v4H8zM8 11h8M8 16h5" },
  { to: "/board", label: "Board", icon: "M3 21h18M5 17V9h4v8M10 17V3h4v14M15 17v-5h4v5" },
  { to: "/dashboard", label: "Admin", icon: "M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" },
  { to: "/console", label: "Console", icon: "M4 7h16M4 17h16M8 4v6M16 14v6" },
];

function useNavItems(): NavItem[] {
  const { player, role, isOwner } = useAuth();
  const signedIn = Boolean(player) || isOwner;
  const isManagerPlus = role === "manager" || role === "admin";
  const leaderboardVisible = useLeaderboardVisible();
  return ALL_ITEMS.filter((item) => {
    if (item.to === "/dashboard") return isManagerPlus;
    if (item.to === "/board") return signedIn && leaderboardVisible;
    if (item.to === "/console") return isOwner;
    return signedIn;
  });
}

/**
 * Bottom tab bar, ported from legacy's <nav class="bottom-bar"> — kept as
 * the SPA's only nav surface at every viewport width. Legacy also drew a
 * desktop-only duplicate in the top bar (`.top-actions.to-bottom`, hidden on
 * mobile); skipped here since a single nav surface avoids ambiguous
 * duplicate-link matches in tests, and this is an internal crew tool rather
 * than something needing device-specific chrome.
 *
 * Role gating: "Admin" needs manager/admin (an owner session gets role
 * "admin" from useAuth's bootstrap — see its comment); "Console" needs an
 * actual owner session specifically, not just any admin-flagged player.
 */
export function BottomNav() {
  const items = useNavItems();

  useEffect(() => {
    document.body.classList.toggle("has-bottom-bar", items.length > 0);
    return () => document.body.classList.remove("has-bottom-bar");
  }, [items.length]);

  const source = <div className="app-source-attribution"><SourceLink>Trim Log source code</SourceLink><span>AGPL-3.0-or-later</span></div>;
  if (!items.length) return source;

  return (
    <>{source}<nav className="bottom-bar" aria-label="Workspace navigation">
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="bb-item"
          activeProps={{ className: "bb-item primary" }}
          activeOptions={{ exact: item.to === "/" }}
        >
          <svg className="bb-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={item.icon} /></svg>
          {item.label}
        </Link>
      ))}
    </nav></>
  );
}
